// super-excalidraw: thin client for the /api/v2/folders endpoints and the
// folderId field on a diagram's meta. Folders are dashboard-only organization;
// a diagram's membership is one field on its existing meta sidecar, so moving
// a diagram is a single PATCH /api/v2/{id}/meta — no folder catalog rewrite.

export type Folder = {
  id: string;
  name: string;
  parentId?: string;
  createdAt: number;
  updatedAt?: number;
};

export type FolderList = {
  items: Folder[];
  updatedAt?: number;
};

const base = "/api/v2/folders";

export async function listFolders(): Promise<Folder[]> {
  const r = await fetch(base + "/");
  if (!r.ok) {
    throw new Error(`folders list failed: ${r.status}`);
  }
  const data: FolderList = await r.json();
  return data.items ?? [];
}

export async function createFolder(
  name: string,
  parentId?: string,
): Promise<Folder> {
  const r = await fetch(base + "/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, parentId: parentId ?? "" }),
  });
  if (!r.ok) {
    throw new Error(`folder create failed: ${r.status}`);
  }
  return r.json();
}

export async function renameFolder(id: string, name: string): Promise<void> {
  const r = await fetch(`${base}/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!r.ok) {
    throw new Error(`folder rename failed: ${r.status}`);
  }
}

export async function moveFolder(
  id: string,
  parentId: string,
): Promise<void> {
  const r = await fetch(`${base}/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ parentId }),
  });
  if (!r.ok) {
    throw new Error(`folder move failed: ${r.status}`);
  }
}

export async function deleteFolder(id: string): Promise<void> {
  const r = await fetch(`${base}/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!r.ok) {
    throw new Error(`folder delete failed: ${r.status}`);
  }
}

// Move a diagram into a folder (folderId "" = unfile to root). Reuses the
// generalized PATCH /api/v2/{id}/meta endpoint.
export async function moveDiagramToFolder(
  diagramId: string,
  folderId: string,
): Promise<void> {
  const r = await fetch(`/api/v2/${encodeURIComponent(diagramId)}/meta`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ folderId }),
  });
  if (!r.ok) {
    throw new Error(`move diagram failed: ${r.status}`);
  }
}

// ─── Media & Library folders ────────────────────────────────────────────────
// Same folder model as diagrams, but two separate catalogs
// (/api/media/folders, /api/library/folders). An item's membership is the
// folderId field on its own record, so moving is one PATCH on the item.

async function listFoldersAt(catalog: string): Promise<Folder[]> {
  const r = await fetch(catalog + "/");
  if (!r.ok) {
    throw new Error(`folders list failed: ${r.status}`);
  }
  const data: FolderList = await r.json();
  return data.items ?? [];
}

async function createFolderAt(
  catalog: string,
  name: string,
  parentId?: string,
): Promise<Folder> {
  const r = await fetch(catalog + "/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, parentId: parentId ?? "" }),
  });
  if (!r.ok) {
    throw new Error(`folder create failed: ${r.status}`);
  }
  return r.json();
}

async function patchFolderAt(
  catalog: string,
  id: string,
  body: Record<string, unknown>,
): Promise<void> {
  const r = await fetch(`${catalog}/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    throw new Error(`folder patch failed: ${r.status}`);
  }
}

async function deleteFolderAt(catalog: string, id: string): Promise<void> {
  const r = await fetch(`${catalog}/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!r.ok) {
    throw new Error(`folder delete failed: ${r.status}`);
  }
}

// Move an item (media or library) into a folder ("" = unfile). The item's
// own PATCH endpoint accepts a folderId pointer.
async function moveItemToFolder(
  itemBase: string,
  id: string,
  folderId: string,
): Promise<void> {
  const r = await fetch(`${itemBase}/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ folderId }),
  });
  if (!r.ok) {
    throw new Error(`move item failed: ${r.status}`);
  }
}

const MEDIA_FOLDERS = "/api/media/folders";
const LIBRARY_FOLDERS = "/api/library/folders";

export const listMediaFolders = () => listFoldersAt(MEDIA_FOLDERS);
export const createMediaFolder = (name: string, parentId?: string) =>
  createFolderAt(MEDIA_FOLDERS, name, parentId);
export const renameMediaFolder = (id: string, name: string) =>
  patchFolderAt(MEDIA_FOLDERS, id, { name });
export const deleteMediaFolder = (id: string) =>
  deleteFolderAt(MEDIA_FOLDERS, id);
export const moveMediaToFolder = (id: string, folderId: string) =>
  moveItemToFolder("/api/media", id, folderId);

export const listLibraryFolders = () => listFoldersAt(LIBRARY_FOLDERS);
export const createLibraryFolder = (name: string, parentId?: string) =>
  createFolderAt(LIBRARY_FOLDERS, name, parentId);
export const renameLibraryFolder = (id: string, name: string) =>
  patchFolderAt(LIBRARY_FOLDERS, id, { name });
export const deleteLibraryFolder = (id: string) =>
  deleteFolderAt(LIBRARY_FOLDERS, id);
export const moveLibraryToFolder = (id: string, folderId: string) =>
  moveItemToFolder("/api/library", id, folderId);
